import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  ChevronDown, ChevronRight, Save, Loader2, 
  CreditCard, Banknote, QrCode, Wallet, ArrowLeftRight, Shield
} from 'lucide-react';
import { Card, Badge } from '../../components/ui';
import { cn } from '../../lib/utils';
import { api } from '../../lib/api';

// Types
interface Resource {
  id: string;
  name: string;
  description: string;
  enabled: boolean;
  limits?: {
    dailyLimit?: number;
    perTransactionLimit?: number;
    monthlyLimit?: number;
  };
}

interface ResourceCategory {
  id: string;
  name: string;
  description: string;
  icon: React.ElementType;
  resources: Resource[];
}

// Default resource categories
const defaultCategories: ResourceCategory[] = [
  {
    id: 'PIX',
    name: 'Pix',
    description: 'Transferências instantâneas via Pix',
    icon: QrCode,
    resources: [
      { id: 'PIX_RECEIVE', name: 'Receber Pix', description: 'Gerar cobranças Pix', enabled: true },
      { id: 'PIX_SEND', name: 'Enviar Pix', description: 'Enviar pagamentos via Pix', enabled: true },
      { id: 'PIX_SCHEDULED', name: 'Pix Agendado', description: 'Agendar pagamentos Pix', enabled: false },
    ],
  },
  {
    id: 'BOLETO',
    name: 'Boleto',
    description: 'Emissão e pagamento de boletos',
    icon: Banknote,
    resources: [
      { id: 'BOLETO_GENERATE', name: 'Gerar Boleto', description: 'Emitir boletos de cobrança', enabled: true },
      { id: 'BOLETO_PAY', name: 'Pagar Boleto', description: 'Pagar boletos', enabled: false },
    ],
  },
  {
    id: 'CARD',
    name: 'Cartões',
    description: 'Operações com cartões',
    icon: CreditCard,
    resources: [
      { id: 'CARD_PAYMENT', name: 'Pagamento Cartão', description: 'Receber pagamentos via cartão', enabled: false },
      { id: 'CARD_ISSUE', name: 'Emissão de Cartão', description: 'Emitir cartões virtuais/físicos', enabled: false },
    ],
  },
  {
    id: 'TRANSFER',
    name: 'Transferências',
    description: 'Transferências entre contas',
    icon: ArrowLeftRight,
    resources: [
      { id: 'TRANSFER_INTERNAL', name: 'Transferência Interna', description: 'Entre contas do mesmo tenant', enabled: true },
      { id: 'TRANSFER_TED', name: 'TED', description: 'Transferência para outros bancos', enabled: false },
    ],
  },
  {
    id: 'CRYPTO',
    name: 'Crypto',
    description: 'Operações com criptomoedas',
    icon: Wallet,
    resources: [
      { id: 'CRYPTO_BUY', name: 'Comprar Crypto', description: 'Comprar criptomoedas', enabled: false },
      { id: 'CRYPTO_SELL', name: 'Vender Crypto', description: 'Vender criptomoedas', enabled: false },
      { id: 'CRYPTO_SWAP', name: 'Swap', description: 'Trocar entre criptomoedas', enabled: false },
      { id: 'CRYPTO_DEFI', name: 'DeFi Pools', description: 'Participar de pools DeFi', enabled: false },
    ],
  },
  {
    id: 'COMPLIANCE',
    name: 'Compliance',
    description: 'Recursos de conformidade',
    icon: Shield,
    resources: [
      { id: 'KYC_BASIC', name: 'KYC Básico', description: 'Verificação básica de identidade', enabled: true },
      { id: 'KYC_ADVANCED', name: 'KYC Avançado', description: 'Verificação avançada com documentos', enabled: false },
    ],
  },
];

interface ResourceConfigProps {
  accountId?: string;
}

export const ResourceConfig: React.FC<ResourceConfigProps> = ({ accountId }) => {
  const queryClient = useQueryClient();
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set(['PIX']));
  const [categories, setCategories] = useState<ResourceCategory[]>(defaultCategories);
  const [hasChanges, setHasChanges] = useState(false);

  // Load existing config
  const { data: configData, isLoading } = useQuery({
    queryKey: ['resource-config', accountId],
    queryFn: async () => {
      const response = await api.get(`/resource-config${accountId ? `?accountId=${accountId}` : ''}`);
      return response.data;
    },
    enabled: !!accountId,
  });

  // Update categories when config data loads
  useEffect(() => {
    if (configData?.resources) {
      // Merge with default categories
      const merged = defaultCategories.map((cat) => ({
        ...cat,
        resources: cat.resources.map((res) => {
          const saved = configData.resources.find((r: Resource) => r.id === res.id);
          return saved ? { ...res, ...saved } : res;
        }),
      }));
      setCategories(merged);
    }
  }, [configData]);

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (config: ResourceCategory[]) => {
      const resources = config.flatMap((cat) => cat.resources);
      return api.post('/resource-config', { accountId, resources });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['resource-config'] });
      setHasChanges(false);
    },
  });

  // Toggle category expansion
  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(categoryId)) {
        next.delete(categoryId);
      } else {
        next.add(categoryId);
      }
      return next;
    });
  };

  // Toggle all resources in a category
  const toggleCategoryResources = (categoryId: string, enabled: boolean) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? { ...cat, resources: cat.resources.map((res) => ({ ...res, enabled })) }
          : cat
      )
    );
    setHasChanges(true);
  };

  // Toggle single resource
  const toggleResource = (categoryId: string, resourceId: string) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              resources: cat.resources.map((res) =>
                res.id === resourceId ? { ...res, enabled: !res.enabled } : res
              ),
            }
          : cat
      )
    );
    setHasChanges(true);
  };

  // Update resource limits
  const updateResourceLimits = (
    categoryId: string,
    resourceId: string,
    limits: Partial<Resource['limits']>
  ) => {
    setCategories((prev) =>
      prev.map((cat) =>
        cat.id === categoryId
          ? {
              ...cat,
              resources: cat.resources.map((res) =>
                res.id === resourceId
                  ? { ...res, limits: { ...res.limits, ...limits } }
                  : res
              ),
            }
          : cat
      )
    );
    setHasChanges(true);
  };

  // Check if all resources in category are enabled
  const isCategoryFullyEnabled = (category: ResourceCategory): boolean => {
    return category.resources.every((res) => res.enabled);
  };

  // Check if any resource in category is enabled
  const isCategoryPartiallyEnabled = (category: ResourceCategory): boolean => {
    return category.resources.some((res) => res.enabled) && !isCategoryFullyEnabled(category);
  };

  // Handle save
  const handleSave = () => {
    saveMutation.mutate(categories);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Configuração de Recursos</h1>
          <p className="text-surface-400 mt-1">
            Habilite ou desabilite recursos e configure limites por operação
          </p>
        </div>
        <button
          onClick={handleSave}
          disabled={!hasChanges || saveMutation.isPending}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all',
            hasChanges
              ? 'bg-primary-500 text-white hover:bg-primary-400'
              : 'bg-surface-700 text-surface-400 cursor-not-allowed'
          )}
        >
          {saveMutation.isPending ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Save className="w-4 h-4" />
          )}
          Salvar Alterações
        </button>
      </div>

      {/* Categories */}
      <div className="space-y-4">
        {categories.map((category) => {
          const Icon = category.icon;
          const isExpanded = expandedCategories.has(category.id);
          const isFullyEnabled = isCategoryFullyEnabled(category);
          const isPartiallyEnabled = isCategoryPartiallyEnabled(category);
          const enabledCount = category.resources.filter((r) => r.enabled).length;

          return (
            <Card key={category.id} className="overflow-hidden">
              {/* Category Header */}
              <div
                className="flex items-center gap-4 p-4 cursor-pointer hover:bg-surface-700/50 transition-colors"
                onClick={() => toggleCategory(category.id)}
              >
                <div className={cn(
                  'p-2 rounded-lg',
                  isFullyEnabled ? 'bg-primary-500/20' : 'bg-surface-700'
                )}>
                  <Icon className={cn(
                    'w-5 h-5',
                    isFullyEnabled ? 'text-primary-400' : 'text-surface-400'
                  )} />
                </div>

                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="text-sm font-medium text-white">{category.name}</h3>
                    <Badge variant={isFullyEnabled ? 'success' : isPartiallyEnabled ? 'warning' : 'default'}>
                      {enabledCount}/{category.resources.length}
                    </Badge>
                  </div>
                  <p className="text-xs text-surface-400">{category.description}</p>
                </div>

                {/* Category Toggle */}
                <label
                  className="relative inline-flex items-center cursor-pointer"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isFullyEnabled}
                    onChange={(e) => toggleCategoryResources(category.id, e.target.checked)}
                    className="sr-only"
                  />
                  <div className={cn(
                    'w-11 h-6 rounded-full transition-colors',
                    isFullyEnabled ? 'bg-primary-500' : isPartiallyEnabled ? 'bg-yellow-500' : 'bg-surface-600'
                  )} />
                  <div className={cn(
                    'absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white transition-transform',
                    isFullyEnabled && 'translate-x-5'
                  )} />
                </label>

                {isExpanded ? (
                  <ChevronDown className="w-5 h-5 text-surface-400" />
                ) : (
                  <ChevronRight className="w-5 h-5 text-surface-400" />
                )}
              </div>

              {/* Resources List */}
              {isExpanded && (
                <div className="border-t border-surface-700">
                  {category.resources.map((resource) => (
                    <div
                      key={resource.id}
                      className={cn(
                        'flex items-center gap-4 px-4 py-3 border-b border-surface-700 last:border-b-0',
                        !resource.enabled && 'opacity-50'
                      )}
                    >
                      <div className="flex-1 pl-12">
                        <p className="text-sm font-medium text-white">{resource.name}</p>
                        <p className="text-xs text-surface-400">{resource.description}</p>
                      </div>

                      {/* Limits (only for enabled resources) */}
                      {resource.enabled && (
                        <div className="flex items-center gap-3">
                          <div className="space-y-1">
                            <label className="text-xs text-surface-500">Limite/Transação</label>
                            <input
                              type="number"
                              value={resource.limits?.perTransactionLimit || ''}
                              onChange={(e) => updateResourceLimits(category.id, resource.id, {
                                perTransactionLimit: e.target.value ? Number(e.target.value) : undefined,
                              })}
                              placeholder="Sem limite"
                              className="w-28 px-2 py-1 bg-surface-800 border border-surface-600 rounded text-xs text-white focus:outline-none focus:border-primary-500"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-surface-500">Limite Diário</label>
                            <input
                              type="number"
                              value={resource.limits?.dailyLimit || ''}
                              onChange={(e) => updateResourceLimits(category.id, resource.id, {
                                dailyLimit: e.target.value ? Number(e.target.value) : undefined,
                              })}
                              placeholder="Sem limite"
                              className="w-28 px-2 py-1 bg-surface-800 border border-surface-600 rounded text-xs text-white focus:outline-none focus:border-primary-500"
                            />
                          </div>
                          <div className="space-y-1">
                            <label className="text-xs text-surface-500">Limite Mensal</label>
                            <input
                              type="number"
                              value={resource.limits?.monthlyLimit || ''}
                              onChange={(e) => updateResourceLimits(category.id, resource.id, {
                                monthlyLimit: e.target.value ? Number(e.target.value) : undefined,
                              })}
                              placeholder="Sem limite"
                              className="w-28 px-2 py-1 bg-surface-800 border border-surface-600 rounded text-xs text-white focus:outline-none focus:border-primary-500"
                            />
                          </div>
                        </div>
                      )}

                      {/* Resource Toggle */}
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={resource.enabled}
                          onChange={() => toggleResource(category.id, resource.id)}
                          className="sr-only"
                        />
                        <div className={cn(
                          'w-9 h-5 rounded-full transition-colors',
                          resource.enabled ? 'bg-primary-500' : 'bg-surface-600'
                        )} />
                        <div className={cn(
                          'absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white transition-transform',
                          resource.enabled && 'translate-x-4'
                        )} />
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </Card>
          );
        })}
      </div>

      {/* Save Status */}
      {saveMutation.isSuccess && (
        <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
          <p className="text-sm text-green-400">Configurações salvas com sucesso!</p>
        </div>
      )}

      {saveMutation.isError && (
        <div className="p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
          <p className="text-sm text-red-400">
            Erro ao salvar: {(saveMutation.error as any)?.message || 'Erro desconhecido'}
          </p>
        </div>
      )}
    </div>
  );
};

export default ResourceConfig;
